import { c as _c } from "react/compiler-runtime";
import type { Blog } from 'api-graphql-types';
import { Card, Heading, IconButton, LayoutCluster, LayoutStack, WaveyWrapper } from 'design-system/client';
import Image from 'next/image';
import { useTranslations, useLocale } from 'next-intl';
import { useCallback, useState } from 'react';
import sanitize, { IFrame } from 'sanitize-html';
import { formatDate } from '../../../helpers';
import styles from './News.module.css';
import TrustedHtml from '@/components/TrustedHtml';
import { NonNullablePicked } from '@/types';
type NewsProps = {
  publicFaceName: string;
  publicFaceImage: string;
  news: Blog[];
};
type PublishedBlog = NonNullablePicked<Blog, 'publishedAt'>;
const ASSET_URL = process.env.NEXT_PUBLIC_ASSET_URL ?? 'https://www.betterplace.dev';
const sanitizeHtmlOptions = {
  allowedTags: sanitize.defaults.allowedTags.concat(['img', 'iframe']),
  allowedAttributes: {
    ...sanitize.defaults.allowedAttributes,
    iframe: sanitize.defaults.allowedAttributes.iframe?.concat(['src', 'width', 'height', 'title']) || ['src', 'width', 'height', 'title']
  },
  allowedIframeHostnames: ['www.youtube.com', 'player.vimeo.com'],
  exclusiveFilter(frame: IFrame) {
    if (frame.tag !== 'img') return false;
    if (!frame.attribs['src']?.startsWith(ASSET_URL)) return true;
    return false;
  }
};
function NewsCard(t0) {
  const $ = _c(39);
  const {
    authorImage,
    authorName,
    publishedAt,
    newsBody: t1,
    newsTitle: t2
  } = t0;
  const newsBody = t1 === undefined ? "" : t1;
  const newsTitle = t2 === undefined ? "" : t2;
  const t = useTranslations("nextjs.receiver_pages.fundraising_event.news");
  const locale = useLocale();
  let T0;
  let T1;
  let T2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== authorImage || $[1] !== authorName || $[2] !== locale || $[3] !== newsTitle || $[4] !== publishedAt || $[5] !== t) {
    const date = formatDate(publishedAt, locale);
    T2 = Card;
    t8 = styles.card;
    T1 = LayoutStack;
    let t9;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = {
        alignSelf: "center"
      };
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    if ($[16] !== newsTitle) {
      t7 = <Heading as="h3" level="heading-sm" style={t9}>{newsTitle}</Heading>;
      $[16] = newsTitle;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    T0 = LayoutCluster;
    t4 = "400";
    t5 = styles.publicFace;
    if ($[18] !== authorImage) {
      t6 = <Image src={authorImage} height={100} width={100} alt="" className={styles.image} />;
      $[18] = authorImage;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    t3 = t("publication_info", {
      manager: authorName,
      date
    });
    $[0] = authorImage;
    $[1] = authorName;
    $[2] = locale;
    $[3] = newsTitle;
    $[4] = publishedAt;
    $[5] = t;
    $[6] = T0;
    $[7] = T1;
    $[8] = T2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    T0 = $[6];
    T1 = $[7];
    T2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
  }
  let t9;
  if ($[20] !== t3) {
    t9 = <p>{t3}</p>;
    $[20] = t3;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== T0 || $[23] !== t4 || $[24] !== t5 || $[25] !== t6 || $[26] !== t9) {
    t10 = <T0 space={t4} className={t5}>{t6}{t9}</T0>;
    $[22] = T0;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== newsBody) {
    t11 = <TrustedHtml value={newsBody} className={styles.textContent} options={sanitizeHtmlOptions} as="div" />;
    $[28] = newsBody;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== T1 || $[31] !== t10 || $[32] !== t11 || $[33] !== t7) {
    t12 = <T1>{t7}{t10}{t11}</T1>;
    $[30] = T1;
    $[31] = t10;
    $[32] = t11;
    $[33] = t7;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== T2 || $[36] !== t12 || $[37] !== t8) {
    t13 = <T2 className={t8}>{t12}</T2>;
    $[35] = T2;
    $[36] = t12;
    $[37] = t8;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  return t13;
}
function PublishedNewsCard(t0) {
  const $ = _c(6);
  const {
    publishedNews,
    publicFaceImage,
    publicFaceName
  } = t0;
  if (!publishedNews) {
    return null;
  }
  const {
    bodyWithMedia,
    title,
    publishedAt,
    author
  } = publishedNews;
  const t1 = author?.imageUrl100x100 ?? publicFaceImage;
  const t2 = author?.name ?? publicFaceName;
  let t3;
  if ($[0] !== bodyWithMedia || $[1] !== publishedAt || $[2] !== t1 || $[3] !== t2 || $[4] !== title) {
    t3 = <NewsCard newsBody={bodyWithMedia} newsTitle={title} publishedAt={publishedAt} authorImage={t1} authorName={t2} />;
    $[0] = bodyWithMedia;
    $[1] = publishedAt;
    $[2] = t1;
    $[3] = t2;
    $[4] = title;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
function hasPublishedDate(blog: Blog): blog is PublishedBlog {
  return !!blog.publishedAt;
}
function News(t0) {
  const $ = _c(20);
  const {
    news,
    publicFaceImage,
    publicFaceName
  } = t0;
  const t = useTranslations("nextjs.receiver_pages.fundraising_event");
  const [page, setPage] = useState(0);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setPage(_temp);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const showNextPage = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setPage(_temp2);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const showPreviousPage = t2;
  let t3;
  if ($[2] !== news) {
    t3 = news.filter(hasPublishedDate);
    $[2] = news;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const publishedNews = t3;
  if (!publishedNews.length) {
    return null;
  }
  let t4;
  if ($[4] !== t) {
    t4 = t("news.title");
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <Heading as="h2" level="heading-sm" className={styles.heading}>{t4}</Heading>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = publishedNews[page];
  let t7;
  if ($[8] !== publicFaceImage || $[9] !== publicFaceName || $[10] !== t6) {
    t7 = <PublishedNewsCard publishedNews={t6} publicFaceImage={publicFaceImage} publicFaceName={publicFaceName} />;
    $[8] = publicFaceImage;
    $[9] = publicFaceName;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== page || $[13] !== publishedNews.length || $[14] !== t) {
    t8 = publishedNews.length > 1 ? <div className={styles.buttonContainer}>{!!page && <IconButton kind="secondary" className={styles.turnPageButton} iconName="chevronLeft" onClick={showPreviousPage} aria-label={t("news.previous_item_button_label")} />}<IconButton kind="secondary" className={styles.turnPageButton} iconName="chevronRight" onClick={showNextPage} aria-label={t("news.next_item_button_label")} disabled={page === publishedNews.length - 1} /></div> : null;
    $[12] = page;
    $[13] = publishedNews.length;
    $[14] = t;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t5 || $[17] !== t7 || $[18] !== t8) {
    t9 = <WaveyWrapper bright={true} topOnly={true} space="400">{t5}{t7}{t8}</WaveyWrapper>;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
}
function _temp2(prevState_0) {
  return prevState_0 - 1;
}
function _temp(prevState) {
  return prevState + 1;
}
export default News;